import React, { useMemo, useCallback } from 'react';
import DeltaArrowIcon from '../icons/DeltaArrowIcon';
import { ExportToCsv } from 'export-to-csv';
import { CSV_EXPORT_DEFAULTS } from '../../utils/DataConstants';

import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	currencyFormatter,
	getWorkOrderBasePriorityBackgroundColor,
	getWorkOrderBasePriorityPillBackgroundColor,
} from '../../utils/DataFormatterUtils';
import { buildLifeCyclePriorityTableColumns } from './LifeCyclePriorityTableColumns';
import { buildLifeCycleSupplierTableColumns } from './LifeCycleSupplierTableColumns';
import {
	MONTH_ABBR,
	MONTH_FULL,
	CHART_METRIC_OPTIONS,
	VIEW_MODE,
	CHART_PRIMARY_COLOR,
	CHART_METRIC,
	PERFORMER_GROUP,
	PERFORMER_SHAPE,
	NEGATIVE_CHANGE_COLOR,
	POSITIVE_CHANGE_COLOR,
} from './LifeCycleAnalyticsConstants';
import {
	formatSupplierName,
	toMinutes,
	formatDurationShort,
	buildSpendTooltipHtml,
	buildSupplierTooltipHtml,
	parseSafeInt,
	parseSafeFloat,
	extractSpendValue,
	roundToOneDecimalPlace,
} from './LifeCycleAnalyticsUtils';
import moment from 'moment';

/** Derived tables, chart series, CSV exports, and dashboard props from useLifeCycleAnalyticsData. */
export function useLifeCycleAnalyticsPresentation(data: any) {
	const {
		companyConfig,
		userType,
		problemTypes,
		fetchProblemTypes,
		history,
		loading,
		chartLoading,
		prioritiesLoading,
		error,
		prioritySummary,
		supplierPerformance,
		monthlyMetricData,
		monthlySpend,
		activeFilters,
		draftFilters,
		filterModalVisible,
		selectedPriorityId,
		minWoCount,
		setMinWoCount,
		viewMode,
		chartMetric,
		createdAtPickerDates,
		completedAtPickerDates,
		isAutoApprovalEnabled,
		priorityMap,
		handleArrowClick,
		handleChartMetricChange,
		navigateToSupplierDetail,
		openFilterModal,
		applyFilters,
		cancelFilters,
		removeProblemTypeFilter,
		handleRemoveCreatedAtFilter,
		handleRemoveCompletedAtFilter,
		handleProblemTypeChange,
		handleCreatedAtCalendarChange,
		handleCreatedAtChange,
		handleCreatedAtOpenChange,
		handleCompletedAtCalendarChange,
		handleCompletedAtChange,
		handleCompletedAtOpenChange,
	} = data;

	const chartColor =
		nullSafeGet('detail.config.theme.navblock', companyConfig) || CHART_PRIMARY_COLOR;

	const hasActiveFilters =
		activeFilters.problemTypeIds.length > 0 ||
		activeFilters.createdAt !== null ||
		activeFilters.completedAt !== null;

	const problemTypeNames = useMemo(() => {
		return activeFilters.problemTypeIds
			.map((id) => nullSafeGetOrElse('name', problemTypes.records[id], null))
			.filter(Boolean)
			.join(', ');
	}, [activeFilters.problemTypeIds, problemTypes.records]);

	const totalCompletedWorkOrders = useMemo(() => {
		return prioritySummary.reduce((sum, item) => sum + (item.completedWoCount || 0), 0);
	}, [prioritySummary]);

	const prioritySummaryCards = useMemo(() => {
		const sorted = prioritySummary.slice().sort((a, b) => a.woPriorityId - b.woPriorityId);
		return sorted.map((item) => {
			const completedWoCount = parseSafeInt(item.completedWoCount);
			const avgApprovalTimeSeconds = parseSafeFloat(item.avgApprovalTimeSeconds);
			const avgResponseTimeSeconds = parseSafeFloat(item.avgResponseTimeSeconds);
			const avgResolutionTimeSeconds = parseSafeFloat(item.avgResolutionTimeSeconds);
			const firstTimeFixRate = parseSafeFloat(item.firstTimeFixRate);
			const share =
				totalCompletedWorkOrders > 0
					? roundToOneDecimalPlace((completedWoCount * 100) / totalCompletedWorkOrders)
					: 0;
			const priority = priorityMap[item.woPriorityId] || { id: Number(item.woPriorityId) };
			const priorityColor = getWorkOrderBasePriorityBackgroundColor(priority);
			return {
				...item,
				completedWoCount,
				avgApprovalTimeSeconds,
				avgResponseTimeSeconds,
				avgResolutionTimeSeconds,
				firstTimeFixRate,
				color: typeof priorityColor === 'string' ? priorityColor : priorityColor.string(),
				bgColor: getWorkOrderBasePriorityPillBackgroundColor(priority),
				share,
			};
		});
	}, [prioritySummary, totalCompletedWorkOrders, priorityMap]);

	const selectedPriorityCard = useMemo(
		() => prioritySummaryCards.find((c) => c.woPriorityId === selectedPriorityId) || null,
		[prioritySummaryCards, selectedPriorityId]
	);

	const tableDataSource = useMemo(() => {
		if (viewMode === VIEW_MODE.ALL) {
			return prioritySummaryCards;
		}
		if (!selectedPriorityCard) {
			return prioritySummaryCards;
		}
		return [selectedPriorityCard];
	}, [prioritySummaryCards, selectedPriorityCard, viewMode]);

	const expandedDropdownData = useMemo(() => {
		if (viewMode !== VIEW_MODE.EXPANDED || !selectedPriorityCard) {
			return [];
		}
		return prioritySummaryCards.filter(
			(item) => item.woPriorityId !== selectedPriorityCard.woPriorityId
		);
	}, [prioritySummaryCards, selectedPriorityCard, viewMode]);

	const priorityTableColumns = useMemo(
		() =>
			buildLifeCyclePriorityTableColumns({
				selectedPriorityId,
				viewMode,
				handleArrowClick,
			}),
		[selectedPriorityId, viewMode, handleArrowClick]
	);

	const availableMetricOptions = useMemo(() => {
		if (isAutoApprovalEnabled) {
			return CHART_METRIC_OPTIONS.filter((m) => m.key !== CHART_METRIC.APPROVAL_TIME);
		}
		return CHART_METRIC_OPTIONS;
	}, [isAutoApprovalEnabled]);

	const activeMetricConfig = useMemo(
		() => availableMetricOptions.find((m) => m.key === chartMetric) || availableMetricOptions[0],
		[chartMetric, availableMetricOptions]
	);

	const chartMetricSummaryValue = useMemo(() => {
		if (selectedPriorityCard) {
			return parseSafeFloat(selectedPriorityCard[activeMetricConfig.dataField]);
		}

		const field = activeMetricConfig.dataField;
		const valid = monthlyMetricData
			.filter((item) => item && !item.isPreviousPeriod && !item.isPreviousYear)
			.map((item) => {
				const val = parseSafeFloat(item[field], NaN);
				if (isNaN(val)) return null;
				return val;
			})
			.filter((v) => v !== null);
		if (valid.length === 0) return 0;
		const avg = valid.reduce((sum, v) => sum + v, 0) / valid.length;
		if (activeMetricConfig.isTimeBased) {
			return Math.round(avg);
		}
		return roundToOneDecimalPlace(avg);
	}, [monthlyMetricData, activeMetricConfig, selectedPriorityCard]);

	const chartMetricDeltaElement = useMemo(() => {
		const changeVal = selectedPriorityCard
			? parseFloat(
					nullSafeGetOrElse(
						activeMetricConfig.changeField,
						nullSafeGetOrElse('changes', selectedPriorityCard, {}),
						''
					)
			  )
			: NaN;
		const hasChange = !isNaN(changeVal) && changeVal !== 0;
		if (!hasChange) return null;
		const isPositive = changeVal > 0;
		const color =
			isPositive !== activeMetricConfig.isTimeBased ? NEGATIVE_CHANGE_COLOR : POSITIVE_CHANGE_COLOR;
		const arrow = <DeltaArrowIcon direction={isPositive ? 'up' : 'down'} color={color} />;
		return (
			<div style={{ color }} className="flex items-center gap-1">
				{arrow} <span className="bodyregular">{roundToOneDecimalPlace(Math.abs(changeVal))}%</span>
			</div>
		);
	}, [selectedPriorityCard, activeMetricConfig]);

	const monthlyMetricChartData = useMemo(() => {
		const field = activeMetricConfig.dataField;
		const isTime = activeMetricConfig.isTimeBased;
		return monthlyMetricData
			.map((item) => {
				const val = parseSafeFloat(item && item[field], NaN);
				if (!item || isNaN(val)) return null;
				return { ...item, _metricRaw: val };
			})
			.filter((item) => !!item && !item.isPreviousPeriod)
			.sort((a, b) => (a.year !== b.year ? a.year - b.year : a.month - b.month))
			.map((item) => ({
				monthLabel: `${MONTH_ABBR[item.month - 1]} '${String(item.year).slice(2)}`,
				monthFull: `${MONTH_FULL[item.month - 1]} ${item.year}`,
				metricValue: isTime ? toMinutes(item._metricRaw) : item._metricRaw,
				metricRaw: item._metricRaw,
			}));
	}, [monthlyMetricData, activeMetricConfig]);

	const spendChartData = useMemo(() => {
		return monthlySpend
			.filter((item) => item && item.year && item.month && !item.isPreviousYear)
			.sort((a, b) => (a.year !== b.year ? a.year - b.year : a.month - b.month))
			.map((item) => {
				const spendValue = extractSpendValue(item);
				return {
					monthLabel: `${MONTH_ABBR[item.month - 1]} '${String(item.year).slice(2)}`,
					monthFull: `${MONTH_FULL[item.month - 1]} ${item.year}`,
					monthKey: `${item.year}-${item.month}`,
					year: String(item.year),
					spendValue,
				};
			});
	}, [monthlySpend]);

	const spendSummary = useMemo(() => {
		if (!monthlySpend || monthlySpend.length === 0) {
			return { totalSpend: 0, percentChange: null };
		}

		let latestTotal = 0;
		let previousTotal = 0;

		monthlySpend.forEach((item) => {
			if (!item || !item.year || !item.month) return;
			const spendValue = extractSpendValue(item);

			if (item.isPreviousYear) {
				previousTotal += spendValue;
			} else {
				latestTotal += spendValue;
			}
		});

		const percentChange =
			previousTotal > 0
				? roundToOneDecimalPlace(((latestTotal - previousTotal) * 100) / previousTotal)
				: null;

		return {
			totalSpend: latestTotal,
			percentChange,
		};
	}, [monthlySpend]);

	const supplierPerformanceAnalytics = useMemo(() => {
		if (!supplierPerformance || supplierPerformance.length === 0) return [];

		return supplierPerformance
			.map((row) => {
				const avgResolutionTimeSeconds = parseSafeFloat(row && row.avgResolutionTimeSeconds, null);
				const avgResponseTimeSeconds = parseSafeFloat(row && row.avgResponseTimeSeconds, null);
				return {
					...row,
					supplierFacilityId: row.supplierFacilityId,
					supplierFacilityName: formatSupplierName(row),
					woCount: row.woCount || 0,
					avgResolutionTimeSeconds: avgResolutionTimeSeconds,
					avgResolutionMinutes: toMinutes(avgResolutionTimeSeconds),
					avgResponseTimeSeconds: avgResponseTimeSeconds === null ? 0 : avgResponseTimeSeconds,
					avgResponseTimeMinutes: toMinutes(
						avgResponseTimeSeconds === null ? 0 : avgResponseTimeSeconds
					),
					isInternalTeam: !!row.isInternalTeam,
					firstTimeFixRate: row.firstTimeFixRate || 0,
				};
			})
			.filter(
				(row) => row.avgResolutionTimeSeconds !== null && row.avgResolutionTimeSeconds !== undefined
			);
	}, [supplierPerformance]);

	const filteredSupplierPerformance = useMemo(() => {
		const filtered = supplierPerformanceAnalytics.filter(
			(row) =>
				(minWoCount <= 0 || row.woCount >= minWoCount) &&
				row.avgResponseTimeSeconds > 0 &&
				row.avgResolutionTimeSeconds > 0
		);
		if (filtered.length === 0) return [];

		const withDist = filtered.map((row, index) => {
			const x =
				row.avgResponseTimeMinutes === null || row.avgResponseTimeMinutes === undefined
					? 0
					: row.avgResponseTimeMinutes;
			const y =
				row.avgResolutionMinutes === null || row.avgResolutionMinutes === undefined
					? 0
					: row.avgResolutionMinutes;
			const id = row.supplierFacilityId !== undefined ? row.supplierFacilityId : index;
			return {
				id,
				distSq: x * x + y * y,
			};
		});

		const sorted = withDist.slice().sort((a, b) => a.distSq - b.distSq);
		const topCount = Math.min(3, sorted.length);
		const bottomCount = Math.min(3, sorted.length);
		const performerGroupById = new Map();

		for (let i = 0; i < topCount; i++) {
			performerGroupById.set(sorted[i].id, PERFORMER_GROUP.TOP);
		}
		for (let i = sorted.length - 1, count = 0; i >= 0 && count < bottomCount; i--) {
			const id = sorted[i].id;
			if (!performerGroupById.has(id)) {
				performerGroupById.set(id, PERFORMER_GROUP.BOTTOM);
				count++;
			}
		}

		return filtered.map((row, index) => {
			const id = row.supplierFacilityId !== undefined ? row.supplierFacilityId : index;
			return {
				...row,
				performerGroup: performerGroupById.get(id) || PERFORMER_GROUP.TYPICAL,
			};
		});
	}, [supplierPerformanceAnalytics, minWoCount]);

	const exportMetricChartCsv = useCallback(() => {
		if (monthlyMetricChartData.length === 0) return;
		const priorityLabel = selectedPriorityCard ? selectedPriorityCard.woPriorityName : 'All';
		const metricLabel = activeMetricConfig.label.replace(/\s+/g, '_');
		const isTime = activeMetricConfig.isTimeBased;
		const headers = isTime
			? ['Month', activeMetricConfig.label + ' (minutes)', activeMetricConfig.label]
			: ['Month', activeMetricConfig.label];
		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: metricLabel + '_' + priorityLabel.replace(/\s+/g, '_'),
			headers: headers,
		});
		const data = monthlyMetricChartData.map((row) => {
			if (isTime) {
				return {
					month: row.monthFull,
					metricMinutes: Math.round(row.metricValue),
					metricFormatted: formatDurationShort(row.metricRaw),
				};
			}
			return {
				month: row.monthFull,
				metricValue: roundToOneDecimalPlace(row.metricRaw),
			};
		});
		csvExporter.generateCsv(data);
	}, [monthlyMetricChartData, selectedPriorityCard, activeMetricConfig]);

	const exportSpendCsv = useCallback(() => {
		if (spendChartData.length === 0) return;
		const priorityLabel = selectedPriorityCard ? selectedPriorityCard.woPriorityName : 'All';
		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: 'Spend_' + priorityLabel.replace(/\s+/g, '_'),
			headers: ['Month', 'Year', 'Spend (USD)'],
		});
		const data = spendChartData.map((row) => {
			return {
				month: row.monthLabel,
				year: row.year,
				spend: row.spendValue,
			};
		});
		csvExporter.generateCsv(data);
	}, [spendChartData, selectedPriorityCard]);

	const exportPrioritySummaryCsv = useCallback(() => {
		if (tableDataSource.length === 0) return;
		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: 'Life_Cycle_Analytics_Priority_Summary',
			headers: [
				'Priority',
				'Completed Work Orders',
				'Avg Approval Time (seconds)',
				'Avg Approval Time',
				'Avg Response Time (seconds)',
				'Avg Response Time',
				'Avg Resolution Time (seconds)',
				'Avg Resolution Time',
				'First Time Fix Rate (%)',
			],
		});
		const data = tableDataSource.map((row) => {
			return {
				priority: row.woPriorityName,
				completedWoCount: row.completedWoCount,
				avgApprovalTimeSeconds: row.avgApprovalTimeSeconds,
				avgApprovalTimeFormatted: formatDurationShort(row.avgApprovalTimeSeconds),
				avgResponseTimeSeconds: row.avgResponseTimeSeconds,
				avgResponseTimeFormatted: formatDurationShort(row.avgResponseTimeSeconds),
				avgResolutionTimeSeconds: row.avgResolutionTimeSeconds,
				avgResolutionTimeFormatted: formatDurationShort(row.avgResolutionTimeSeconds),
				firstTimeFixRate: roundToOneDecimalPlace(row.firstTimeFixRate || 0),
			};
		});
		csvExporter.generateCsv(data);
	}, [tableDataSource]);

	const exportSupplierPerformanceCsv = useCallback(() => {
		const dataToExport = filteredSupplierPerformance;
		if (dataToExport.length === 0) return;
		const priorityLabel = selectedPriorityCard ? selectedPriorityCard.woPriorityName : 'All';
		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: 'WO_Performance_' + priorityLabel.replace(/\s+/g, '_'),
			headers: [
				'Supplier',
				'WOs',
				'Avg Response Time (seconds)',
				'Avg Response Time',
				'Avg Resolution Time (seconds)',
				'Avg Resolution Time',
				'Performer Group',
			],
		});
		const data = dataToExport.map((row) => {
			return {
				supplier: row.supplierFacilityName,
				woCount: row.woCount,
				avgResponseTimeSeconds: row.avgResponseTimeSeconds,
				avgResponseTimeFormatted: formatDurationShort(row.avgResponseTimeSeconds),
				avgResolutionTimeSeconds: row.avgResolutionTimeSeconds,
				avgResolutionTimeFormatted: formatDurationShort(row.avgResolutionTimeSeconds),
				performerGroup: row.performerGroup,
			};
		});
		csvExporter.generateCsv(data);
	}, [filteredSupplierPerformance, selectedPriorityCard]);
	const handleChartMetricClick = useCallback(
		({ key }) => handleChartMetricChange(key),
		[handleChartMetricChange]
	);
	const formatMetricAxisLabel = useCallback(
		(val) => {
			const num = parseFloat(String(val));
			if (isNaN(num)) return `${val}`;
			if (activeMetricConfig.isTimeBased) {
				return formatDurationShort(num * 60);
			}
			return `${roundToOneDecimalPlace(num)}%`;
		},
		[activeMetricConfig]
	);

	const handleSpendIntervalClick = useCallback(
		(ev) => {
			const data = nullSafeGet('data.data', ev);
			const monthKey = data && data.monthKey;
			if (monthKey) {
				const startDate = moment(monthKey, 'YYYY-M').startOf('month').format('YYYY-MM-DD');
				const endDate = moment(monthKey, 'YYYY-M').endOf('month').format('YYYY-MM-DD');
				history.push(
					'/' +
						userType +
						'/analytics/spend/overview/problemTypes?startDate=' +
						startDate +
						'&endDate=' +
						endDate +
						'&sortBy=spend&sortOrder=desc'
				);
			}
		},
		[history, userType]
	);

	const formatSpendAxisLabel = useCallback((val) => currencyFormatter(0, 'USD')(val), []);

	const buildSpendTooltip = useCallback(
		(monthFull, spendValue, monthKey) => {
			const startDate = moment(monthKey, 'YYYY-M').startOf('month').format('YYYY-MM-DD');
			const endDate = moment(monthKey, 'YYYY-M').endOf('month').format('YYYY-MM-DD');
			const detailUrl =
				'/' +
				userType +
				'/analytics/spend/overview/problemTypes?startDate=' +
				startDate +
				'&endDate=' +
				endDate +
				'&sortBy=spend&sortOrder=desc';
			return {
				name: monthFull,
				value: buildSpendTooltipHtml(monthFull, currencyFormatter(0, 'USD')(spendValue), detailUrl),
			};
		},
		[userType]
	);

	const handleWoPerformancePointClick = useCallback(
		(ev) => {
			const data = nullSafeGet('data.data', ev);
			navigateToSupplierDetail(data && data.supplierFacilityId);
		},
		[navigateToSupplierDetail]
	);

	const formatDurationAxisLabel = useCallback((val) => {
		const minutes = parseFloat(String(val));
		if (isNaN(minutes)) return `${val}`;
		return formatDurationShort(minutes * 60);
	}, []);

	const buildPerformanceTooltip = useCallback(
		(name, woCount, responseSeconds, resolutionSeconds) => ({
			name: name,
			value: buildSupplierTooltipHtml(name, woCount, responseSeconds, resolutionSeconds),
		}),
		[]
	);

	const formatPerformerShape = useCallback((performerGroup, isInternalTeam) => {
		if (isInternalTeam) return PERFORMER_SHAPE.STAR;
		if (performerGroup === PERFORMER_GROUP.TOP) return PERFORMER_SHAPE.STAR;
		if (performerGroup === PERFORMER_GROUP.BOTTOM) return PERFORMER_SHAPE.CROSS;
		return PERFORMER_SHAPE.CIRCLE;
	}, []);

	const formatPerformerSize = useCallback((performerGroup, isInternalTeam) => {
		if (isInternalTeam || performerGroup === PERFORMER_GROUP.TOP) return 6;
		return 5;
	}, []);

	const formatPerformerStyle = useCallback(
		(performerGroup, isInternalTeam) => {
			const color = chartColor;
			if (performerGroup === PERFORMER_GROUP.TOP) {
				return { fill: color, stroke: color };
			}
			if (performerGroup === PERFORMER_GROUP.TYPICAL) {
				return {
					lineWidth: 1.5,
					fill: 'transparent',
					stroke: color,
				};
			}
			if (performerGroup === PERFORMER_GROUP.BOTTOM) {
				return {
					lineWidth: 1.5,
					fill: null,
					stroke: color,
				};
			}
			return {
				lineWidth: 1.5,
				fill: color,
				stroke: color,
			};
		},
		[chartColor]
	);

	const renderWoCount = useCallback((value) => value || 0, []);
	const renderDurationColumn = useCallback((value) => formatDurationShort(value || 0), []);
	const renderFirstTimeFixRate = useCallback((value) => {
		const num = parseFloat(String(value));
		return isNaN(num) ? '-' : `${roundToOneDecimalPlace(num)}%`;
	}, []);
	const sortWoCount = useCallback((a, b) => a.woCount - b.woCount, []);
	const sortAvgResponseTime = useCallback(
		(a, b) => a.avgResponseTimeSeconds - b.avgResponseTimeSeconds,
		[]
	);
	const sortAvgResolutionTime = useCallback(
		(a, b) => a.avgResolutionTimeSeconds - b.avgResolutionTimeSeconds,
		[]
	);
	const sortFirstTimeFixRate = useCallback(
		(a, b) => parseFloat(a.firstTimeFixRate || 0) - parseFloat(b.firstTimeFixRate || 0),
		[]
	);

	const handleRowClick = useCallback(
		(record) => ({
			onClick: () => handleArrowClick(record.woPriorityId),
			style: { cursor: 'pointer' },
		}),
		[handleArrowClick]
	);

	const renderSupplierLink = useCallback(
		(text, record) => {
			const supplierFacilityId = record.supplierFacilityId;
			if (!supplierFacilityId) return text;
			return (
				<div
					className="cursor-pointer text-inherit no-underline hover:underline"
					onClick={(e) => {
						e.preventDefault();
						navigateToSupplierDetail(supplierFacilityId);
					}}
				>
					{text}
				</div>
			);
		},
		[navigateToSupplierDetail]
	);

	const supplierTableColumns = useMemo(
		() =>
			buildLifeCycleSupplierTableColumns({
				filteredSupplierPerformanceLength: filteredSupplierPerformance.length,
				renderSupplierLink,
				renderWoCount,
				sortWoCount,
				sortAvgResponseTime,
				renderDurationColumn,
				sortAvgResolutionTime,
				sortFirstTimeFixRate,
				renderFirstTimeFixRate,
			}),
		[
			filteredSupplierPerformance.length,
			renderSupplierLink,
			renderWoCount,
			sortWoCount,
			sortAvgResponseTime,
			renderDurationColumn,
			sortAvgResolutionTime,
			sortFirstTimeFixRate,
			renderFirstTimeFixRate,
		]
	);

	const showCharts = totalCompletedWorkOrders > 0 && viewMode !== VIEW_MODE.ALL;

	return {
		companyConfig,
		dashboard: {
			spinning: loading || chartLoading || prioritiesLoading,
			error,
			prioritySummaryProps: {
				loading,
				totalCompletedWorkOrders,
				prioritySummaryCards,
				tableDataSource,
				expandedDropdownData,
				priorityTableColumns,
				viewMode,
				hasActiveFilters,
				activeFilters,
				problemTypeNames,
				handleArrowClick,
				handleRowClick,
				openFilterModal,
				exportPrioritySummaryCsv,
				removeProblemTypeFilter,
				handleRemoveCreatedAtFilter,
				handleRemoveCompletedAtFilter,
			},
			chartsAndPerformanceProps: {
				showCharts,
				metricChartProps: {
					chartColor,
					chartMetric,
					activeMetricConfig,
					chartMetricSummaryValue,
					chartMetricDeltaElement,
					monthlyMetricChartData,
					selectedPriorityCard,
					formatMetricAxisLabel,
					handleChartMetricClick,
					exportMetricChartCsv,
					availableMetricOptions,
				},
				spendChartProps: {
					chartColor,
					spendChartData,
					spendSummary,
					selectedPriorityCard,
					formatSpendAxisLabel,
					buildSpendTooltip,
					handleSpendIntervalClick,
					exportSpendCsv,
				},
				woPerformanceProps: {
					chartColor,
					filteredSupplierPerformance,
					supplierTableColumns,
					selectedPriorityCard,
					minWoCount,
					setMinWoCount,
					formatDurationAxisLabel,
					formatPerformerShape,
					formatPerformerSize,
					formatPerformerStyle,
					buildPerformanceTooltip,
					handleWoPerformancePointClick,
					exportSupplierPerformanceCsv,
				},
			},
		},
		filterDrawer: {
			visible: filterModalVisible,
			draftFilters,
			problemTypes,
			createdAtPickerDates,
			completedAtPickerDates,
			fetchProblemTypes,
			handleProblemTypeChange,
			handleCreatedAtCalendarChange,
			handleCreatedAtChange,
			handleCreatedAtOpenChange,
			handleCompletedAtCalendarChange,
			handleCompletedAtChange,
			handleCompletedAtOpenChange,
			applyFilters,
			cancelFilters,
		},
	};
}
